import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function FixingSecurityHeadersGuide() {
  return (
    <div>
      <SEO
        title="How to Add Security Headers - Cloudflare, Vercel, AWS, Netlify & More"
        description="Step-by-step guides to add security headers (HSTS, CSP, X-Frame-Options) on popular hosting platforms. Improve your website security score and email deliverability."
        canonical="/guides/fixing-security-headers"
        ogType="article"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-teal-50/50 to-transparent py-16 dark:from-teal-950/20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Help Center
          </Link>
          <h1 className="mt-8 text-4xl font-bold tracking-tight sm:text-5xl">
            How to Add Security Headers
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
            Step-by-step guides for Cloudflare, Vercel, AWS, Netlify, Nginx, Apache, and more.
            Get your security score to A+ in minutes.
          </p>
        </div>
      </section>

      {/* Quick Reference */}
      <section className="border-b bg-muted/30 py-8">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <p className="text-sm font-medium text-muted-foreground">Jump to:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="#cloudflare" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">Cloudflare</a>
            <a href="#vercel" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">Vercel</a>
            <a href="#netlify" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">Netlify</a>
            <a href="#aws" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">AWS CloudFront</a>
            <a href="#nginx" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">Nginx</a>
            <a href="#apache" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">Apache</a>
            <a href="#wordpress" className="rounded-full bg-background px-3 py-1 text-sm font-medium border hover:border-teal-500 hover:text-teal-600">WordPress</a>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">

          {/* Headers Reference */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Security Headers Quick Reference
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              These are the key security headers you need to implement. Copy these values for
              the platform guides below.
            </p>

            <div className="mt-8 space-y-4">
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/50 px-4 py-2">
                  <code className="text-sm font-semibold text-teal-600">Strict-Transport-Security</code>
                  <span className="ml-2 text-xs text-muted-foreground">(Critical)</span>
                </div>
                <div className="p-4">
                  <code className="block rounded bg-muted px-3 py-2 text-sm">
                    max-age=31536000; includeSubDomains; preload
                  </code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Forces HTTPS for your domain. The max-age is in seconds (1 year).
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/50 px-4 py-2">
                  <code className="text-sm font-semibold text-teal-600">Content-Security-Policy</code>
                  <span className="ml-2 text-xs text-muted-foreground">(Critical)</span>
                </div>
                <div className="p-4">
                  <code className="block rounded bg-muted px-3 py-2 text-sm break-all">
                    default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:; connect-src 'self'
                  </code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Controls which resources can load. Start permissive and tighten as needed.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/50 px-4 py-2">
                  <code className="text-sm font-semibold text-teal-600">X-Frame-Options</code>
                  <span className="ml-2 text-xs text-muted-foreground">(High)</span>
                </div>
                <div className="p-4">
                  <code className="block rounded bg-muted px-3 py-2 text-sm">DENY</code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Prevents your site from being embedded in iframes. Use SAMEORIGIN if you need iframes internally.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/50 px-4 py-2">
                  <code className="text-sm font-semibold text-teal-600">X-Content-Type-Options</code>
                  <span className="ml-2 text-xs text-muted-foreground">(High)</span>
                </div>
                <div className="p-4">
                  <code className="block rounded bg-muted px-3 py-2 text-sm">nosniff</code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Prevents browsers from MIME-sniffing content types.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/50 px-4 py-2">
                  <code className="text-sm font-semibold text-teal-600">Referrer-Policy</code>
                  <span className="ml-2 text-xs text-muted-foreground">(Medium)</span>
                </div>
                <div className="p-4">
                  <code className="block rounded bg-muted px-3 py-2 text-sm">strict-origin-when-cross-origin</code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Controls what information is sent in the Referer header.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/50 px-4 py-2">
                  <code className="text-sm font-semibold text-teal-600">Permissions-Policy</code>
                  <span className="ml-2 text-xs text-muted-foreground">(Medium)</span>
                </div>
                <div className="p-4">
                  <code className="block rounded bg-muted px-3 py-2 text-sm break-all">
                    accelerometer=(), camera=(), geolocation=(), gyroscope=(), magnetometer=(), microphone=(), payment=(), usb=()
                  </code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Disables browser features you don't use.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Cloudflare */}
          <section id="cloudflare" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10">
                <svg className="h-6 w-6 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5088 16.8447c.1475-.5068.0908-.9707-.1553-1.3154-.2246-.3164-.5765-.502-.9658-.502h-5.725c-.0615 0-.1113-.0489-.1191-.1123-.0039-.0313.0039-.0615.0234-.0879.0195-.0264.0468-.0439.0781-.0498l5.8555-.0703c.8936-.0303 1.8545-.7236 2.1914-1.5908l.4307-1.1856c.0088-.0205.0117-.043.0088-.0654-.0694-.3848-.2793-.7354-.5928-1.0186-.3134-.2842-.7148-.4658-1.1358-.5136L5.415 10.1787c-.0654-.0078-.1259.0352-.1396.0996l-.5225 1.8564c-.1475.5068-.0908.9707.1543 1.3164.2237.3164.5765.5029.9658.5029l5.7256.0703c.0303.0039.0596.0166.0791.0449a.1126.1126 0 0 1-.0098.1406c-.0185.0263-.0459.0438-.0771.0488l-5.8565.0713c-.8926.0303-1.8535.7237-2.1895 1.5899l-.5752 1.5908c-.0205.0557.0225.1152.082.1211l12.083.0713c.8682 0 1.7637-.3554 2.4854-.9882l.1231-.109.1904-.2246c.2246-.2783.4043-.5908.5312-.9219l.2598-.7295c.0909-.2598.0468-.5039-.1202-.6641-.1641-.1611-.4209-.2285-.6797-.1787-.2598.0508-.4805.2148-.5752.4482l-.2598.7178c-.0849.2344-.3164.3711-.5703.3379-.2529-.0342-.4385-.2354-.4571-.4873l.0117-4.0136c0-.0654-.0479-.1211-.1094-.1221-.0205-.002-.0449.002-.0654.0108l-4.0293 2.1269c-.0273.0146-.0459.0391-.0527.0674-.0029.0127-.0029.0283.0019.041.0459.2549.0156.5196-.0849.7529l-.5801 1.3526c-.1016.2451-.0781.5078.0654.7197.1435.2128.3867.3418.6553.3486.2695.0078.5195-.1123.6738-.3242l1.1309-1.5205c.0313-.0429.0918-.0517.1348-.0205.0215.0166.0351.0401.0381.0654l.0869 1.2949c.0147.2294.1338.4374.3213.5615.1875.1242.4199.1563.6289.0889.2081-.0664.3741-.2246.4482-.4277l.9248-2.5449c.0166-.0449.0557-.0771.1025-.0849.0468-.0078.0957.0098.127.0459.0264.0303.039.0693.0342.1094l-.1982 1.5703c-.0127.2354.0634.4639.211.6338.1475.1699.3584.2764.5859.2959h.0205c.4385 0 .8232-.3008.9365-.7275z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Cloudflare</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Cloudflare makes it easy to add security headers with Transform Rules. No code required.
            </p>

            <div className="mt-8 space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">Option 1: Transform Rules (Recommended)</h3>
                <ol className="mt-4 space-y-4 text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">1</span>
                    <span>Go to your Cloudflare dashboard → Select your domain</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">2</span>
                    <span>Navigate to <strong>Rules</strong> → <strong>Transform Rules</strong> → <strong>Modify Response Header</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">3</span>
                    <span>Click <strong>Create rule</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">4</span>
                    <span>Set "When incoming requests match" to <strong>All incoming requests</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">5</span>
                    <span>Under "Then", add each header from the reference above using "Set static"</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">6</span>
                    <span>Click <strong>Deploy</strong></span>
                  </li>
                </ol>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">Option 2: Cloudflare Worker</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  For more control, use a Worker to add headers:
                </p>
                <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`addEventListener('fetch', event => {
  event.respondWith(handleRequest(event.request))
})

async function handleRequest(request) {
  const response = await fetch(request)
  const newHeaders = new Headers(response.headers)

  newHeaders.set('Strict-Transport-Security',
    'max-age=31536000; includeSubDomains; preload')
  newHeaders.set('X-Frame-Options', 'DENY')
  newHeaders.set('X-Content-Type-Options', 'nosniff')
  newHeaders.set('Referrer-Policy',
    'strict-origin-when-cross-origin')
  newHeaders.set('Permissions-Policy',
    'camera=(), microphone=(), geolocation=()')

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers: newHeaders
  })
}`}
                </pre>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Vercel */}
          <section id="vercel" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-black dark:bg-white">
                <svg className="h-6 w-6 text-white dark:text-black" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 22.525H0l12-21.05 12 21.05z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Vercel</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Add security headers via <code className="rounded bg-muted px-1.5 py-0.5 text-sm">vercel.json</code> or <code className="rounded bg-muted px-1.5 py-0.5 text-sm">next.config.js</code>.
            </p>

            <div className="mt-8 space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">vercel.json</h3>
                <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`{
  "headers": [
    {
      "source": "/(.*)",
      "headers": [
        {
          "key": "Strict-Transport-Security",
          "value": "max-age=31536000; includeSubDomains; preload"
        },
        {
          "key": "X-Frame-Options",
          "value": "DENY"
        },
        {
          "key": "X-Content-Type-Options",
          "value": "nosniff"
        },
        {
          "key": "Referrer-Policy",
          "value": "strict-origin-when-cross-origin"
        },
        {
          "key": "Permissions-Policy",
          "value": "camera=(), microphone=(), geolocation=()"
        }
      ]
    }
  ]
}`}
                </pre>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">Next.js (next.config.js)</h3>
                <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`module.exports = {
  async headers() {
    return [
      {
        source: '/:path*',
        headers: [
          {
            key: 'Strict-Transport-Security',
            value: 'max-age=31536000; includeSubDomains; preload'
          },
          {
            key: 'X-Frame-Options',
            value: 'DENY'
          },
          {
            key: 'X-Content-Type-Options',
            value: 'nosniff'
          },
          {
            key: 'Referrer-Policy',
            value: 'strict-origin-when-cross-origin'
          }
        ]
      }
    ]
  }
}`}
                </pre>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Netlify */}
          <section id="netlify" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-500/10">
                <svg className="h-6 w-6 text-teal-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.3877 10.5273L16.7756 10.8424L16.7616 10.5483L17.3877 10.5273ZM17.1168 10.0752L17.3386 10.2849L17.1448 10.5203L16.923 10.3106L17.1168 10.0752ZM13.4004 3.15234L14.2783 3.18848L13.4004 3.15234ZM6.36816 10.2534L6.56494 10.5273L6.36816 10.2534ZM13.8955 10.3071L13.7578 10.5343L13.8955 10.3071ZM9.44824 9.87207L9.58594 9.64453L9.44824 9.87207ZM16.7793 10.5062V10.8424L16.5361 10.5062H16.7793ZM5.62988 10.9395L5.7998 10.6621L5.62988 10.9395ZM16.3926 10.2289L16.5361 10.0752L16.6797 10.2114L16.5361 10.3651L16.3926 10.2289ZM5.16992 10.2534L5.31445 10.4141L5.16992 10.2534ZM17.3877 10.2114V10.5273L17.1445 10.2114H17.3877ZM14.5039 3.15234L13.6992 3.27344L14.5039 3.15234ZM16.3926 10.5343V10.8424H16.1494L16.2998 10.5553L16.3926 10.5343ZM14.8311 10.5518L14.7031 10.7754L14.5234 10.6621L14.6514 10.4385L14.8311 10.5518ZM16.2998 10.2114L16.4502 10.0752L16.6006 10.2289L16.4502 10.3826L16.2998 10.2114ZM13.9482 10.8424V10.5273L14.1914 10.8424H13.9482ZM6.57178 10.5693V10.2534L6.81494 10.5693H6.57178ZM17.3457 10.8424V10.5273L17.5889 10.8424H17.3457ZM13.4004 3.5625V3.15234L13.6436 3.5625H13.4004ZM7.34766 10.2604L7.54443 10.0262L7.73828 10.2359L7.54443 10.4701L7.34766 10.2604ZM6.85547 10.0332L7.05225 9.80566L7.2461 10.0088L7.05225 10.2359L6.85547 10.0332ZM13.1572 3.15234V3.5625L12.9141 3.15234H13.1572ZM5.41602 10.5693L5.55566 10.7861L5.32812 10.9395L5.18848 10.7227L5.41602 10.5693ZM13.1572 10.5693V10.8424L12.9141 10.5693H13.1572ZM8.18555 10.0332L8.38232 9.80566L8.57617 10.0332L8.38232 10.2604L8.18555 10.0332ZM17.3457 10.5273V10.8424L17.1025 10.5273H17.3457ZM16.0566 10.0752L16.2998 10.2114L16.1152 10.4385L15.8721 10.3022L16.0566 10.0752ZM8.77246 10.2359L8.96924 10.4631L8.77539 10.6902L8.57861 10.4631L8.77246 10.2359ZM5.80957 11.3496L5.58203 11.5768L5.44238 11.3076L5.66992 11.0803L5.80957 11.3496ZM12.9141 10.5693V10.8424L12.6709 10.5693H12.9141ZM6.57178 10.2534V10.5693L6.32861 10.2534H6.57178ZM7.12305 10.5343L6.92627 10.7614L6.73242 10.5343L6.92627 10.3071L7.12305 10.5343ZM16.0566 10.3022L16.2998 10.0752L16.4434 10.3022L16.2998 10.5273L16.0566 10.3022ZM15.5234 10.0332L15.7656 9.80566L15.959 10.0332L15.7656 10.2604L15.5234 10.0332ZM16.5869 10.0752L16.7793 10.2849L16.5869 10.5273L16.3945 10.3176L16.5869 10.0752ZM13.9482 10.5273V10.8424L13.7051 10.5273H13.9482ZM9.01855 10.5343L9.21533 10.3071L9.40918 10.5343L9.21533 10.7614L9.01855 10.5343ZM14.1914 10.5273V10.8424L13.9482 10.5273H14.1914ZM14.4346 10.5273V10.8424L14.1914 10.5273H14.4346ZM9.50586 10.2359L9.70264 10.0088L9.89648 10.2359L9.70264 10.4631L9.50586 10.2359ZM7.45117 10.5343L7.24854 10.3071L7.45117 10.0799L7.65381 10.3071L7.45117 10.5343ZM5.41602 11.0803L5.58594 10.8042L5.82031 10.9395L5.65039 11.2156L5.41602 11.0803ZM14.6777 10.5273V10.8424L14.4346 10.5273H14.6777ZM6.12891 10.2359L6.32568 10.0088L6.51953 10.2359L6.32568 10.4631L6.12891 10.2359ZM7.78125 10.2604L7.97803 10.0262L8.17188 10.2604L7.97803 10.4946L7.78125 10.2604Z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Netlify</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Add a <code className="rounded bg-muted px-1.5 py-0.5 text-sm">_headers</code> file to your publish directory.
            </p>

            <div className="mt-8 rounded-xl border bg-card p-6">
              <h3 className="font-semibold">_headers file</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Create this file in your build output folder (usually <code>public/</code> or <code>dist/</code>):
              </p>
              <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`/*
  Strict-Transport-Security: max-age=31536000; includeSubDomains; preload
  X-Frame-Options: DENY
  X-Content-Type-Options: nosniff
  Referrer-Policy: strict-origin-when-cross-origin
  Permissions-Policy: camera=(), microphone=(), geolocation=()
  Content-Security-Policy: default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:`}
              </pre>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* AWS CloudFront */}
          <section id="aws" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10">
                <svg className="h-6 w-6 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.296.072-.583.16-.863.279a2.04 2.04 0 0 1-.248.104c-.08.032-.143.048-.191.048-.168 0-.256-.12-.256-.367v-.391c0-.192.024-.335.08-.423.056-.088.16-.168.311-.247.28-.144.614-.263 1.005-.36a4.84 4.84 0 0 1 1.246-.151c.95 0 1.644.216 2.091.647.439.431.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 0 0-.735-.136 6.02 6.02 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.216 0-.36-.032-.44-.104-.08-.072-.152-.2-.216-.399l-2.403-7.907a1.87 1.87 0 0 1-.088-.439c0-.175.087-.271.263-.271h.591c.224 0 .376.032.448.104.08.063.143.192.207.399l1.716 6.763 1.596-6.763c.056-.208.12-.336.2-.399.079-.072.24-.104.455-.104h.479c.224 0 .376.032.456.104.08.063.151.192.2.399l1.612 6.843 1.772-6.843c.064-.208.136-.336.208-.399.08-.072.232-.104.447-.104h.559c.176 0 .271.088.271.271 0 .055-.008.111-.024.176a1.1 1.1 0 0 1-.072.263l-2.467 7.907c-.064.2-.136.327-.216.399-.08.072-.232.104-.448.104h-.511c-.224 0-.376-.032-.456-.104-.08-.071-.151-.2-.2-.407l-1.58-6.59-1.572 6.582c-.056.208-.12.336-.2.407-.079.072-.24.104-.455.104h-.511zm10.324.248a4.64 4.64 0 0 1-1.077-.12 3.42 3.42 0 0 1-.86-.299c-.12-.072-.208-.152-.247-.232a.593.593 0 0 1-.064-.256v-.407c0-.247.096-.367.279-.367.072 0 .144.016.216.04.072.023.176.063.28.103.382.16.79.271 1.213.343.43.072.854.112 1.285.112.68 0 1.205-.12 1.572-.351.368-.232.558-.567.558-.999 0-.295-.095-.535-.287-.718-.191-.184-.551-.352-1.077-.504l-1.548-.48c-.775-.24-1.349-.6-1.708-1.076-.36-.478-.543-1.006-.543-1.58 0-.456.096-.862.295-1.206.2-.343.47-.638.815-.879.344-.24.735-.424 1.188-.558.455-.136.95-.2 1.477-.2.264 0 .535.016.806.048.279.04.535.088.782.152.24.064.471.136.695.216.224.08.399.16.528.24.175.103.295.207.36.311a.71.71 0 0 1 .095.368v.375c0 .248-.095.375-.279.375a1.26 1.26 0 0 1-.463-.152 5.44 5.44 0 0 0-2.299-.479c-.614 0-1.101.096-1.453.287-.351.192-.534.478-.534.878 0 .296.112.535.327.726.216.192.607.376 1.173.535l1.517.455c.766.231 1.317.567 1.66 1.006.343.44.511.934.511 1.486 0 .464-.096.878-.28 1.237-.19.359-.454.678-.806.943-.351.264-.775.464-1.269.607-.503.136-1.045.208-1.636.208z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">AWS CloudFront</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Use a Response Headers Policy to add security headers.
            </p>

            <div className="mt-8 rounded-xl border bg-card p-6">
              <h3 className="font-semibold">Steps</h3>
              <ol className="mt-4 space-y-4 text-muted-foreground">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">1</span>
                  <span>Go to CloudFront Console → <strong>Policies</strong> → <strong>Response headers</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">2</span>
                  <span>Click <strong>Create response headers policy</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">3</span>
                  <span>Under <strong>Security headers</strong>, enable the options and set values from the reference above</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">4</span>
                  <span>Go to your CloudFront distribution → <strong>Behaviors</strong> → Edit behavior</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-sm font-semibold text-teal-600">5</span>
                  <span>Attach your new policy under <strong>Response headers policy</strong></span>
                </li>
              </ol>

              <div className="mt-6 rounded-lg bg-amber-50 p-4 dark:bg-amber-950/30">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Tip:</strong> AWS also offers pre-built security headers policies.
                  Look for "SecurityHeadersPolicy" when selecting a response headers policy.
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Nginx */}
          <section id="nginx" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500/10">
                <svg className="h-6 w-6 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L1.605 6v12L12 24l10.395-6V6L12 0zm6 16.59c0 .705-.646 1.29-1.529 1.29-.631 0-1.351-.255-1.801-.81l-6-7.141v6.66c0 .721-.57 1.29-1.274 1.29H7.32c-.721 0-1.29-.6-1.29-1.29V7.41c0-.705.63-1.29 1.5-1.29.646 0 1.38.255 1.83.81l5.97 7.141V7.41c0-.721.6-1.29 1.29-1.29h.075c.72 0 1.29.6 1.29 1.29v9.18h.015z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Nginx</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Add headers in your nginx.conf or site configuration file.
            </p>

            <div className="mt-8 rounded-xl border bg-card p-6">
              <h3 className="font-semibold">nginx.conf / site config</h3>
              <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`server {
    listen 443 ssl http2;
    server_name example.com;

    # Security Headers
    add_header Strict-Transport-Security "max-age=31536000; includeSubDomains; preload" always;
    add_header X-Frame-Options "DENY" always;
    add_header X-Content-Type-Options "nosniff" always;
    add_header Referrer-Policy "strict-origin-when-cross-origin" always;
    add_header Permissions-Policy "camera=(), microphone=(), geolocation=()" always;
    add_header Content-Security-Policy "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:" always;

    # ... rest of your configuration
}`}
              </pre>
              <p className="mt-4 text-sm text-muted-foreground">
                After editing, test with <code className="rounded bg-muted px-1.5 py-0.5">nginx -t</code> and reload with <code className="rounded bg-muted px-1.5 py-0.5">nginx -s reload</code>
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Apache */}
          <section id="apache" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/10">
                <svg className="h-6 w-6 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10.727 14.051a.198.198 0 0 0-.054-.066c-.076-.043-.193-.013-.298.054-.054.041-.093.088-.117.131-.033.043-.048.088-.043.131.006.046.028.068.07.088.044.024.1.02.161-.017.054-.033.1-.078.133-.131.032-.054.044-.114.044-.161a.134.134 0 0 0-.051-.087c.015-.018.015-.035 0-.052.045.039.1.068.155.087zm-.7-1.02c.145-.1.29-.175.432-.227.145-.054.274-.08.395-.08.102 0 .187.022.256.067.066.045.118.11.151.189.032.078.05.172.05.274 0 .105-.018.215-.054.33-.033.114-.083.227-.147.34a1.62 1.62 0 0 1-.227.305c-.089.093-.185.172-.29.238-.106.066-.213.115-.324.149-.11.035-.215.052-.314.052-.102 0-.195-.017-.277-.052a.52.52 0 0 1-.198-.156.573.573 0 0 1-.106-.23 1.166 1.166 0 0 1-.027-.282c0-.127.024-.26.067-.4.044-.14.11-.27.2-.392.087-.121.188-.227.303-.318z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Apache</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Add headers in your .htaccess file or Apache configuration.
            </p>

            <div className="mt-8 rounded-xl border bg-card p-6">
              <h3 className="font-semibold">.htaccess</h3>
              <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`<IfModule mod_headers.c>
    Header always set Strict-Transport-Security "max-age=31536000; includeSubDomains; preload"
    Header always set X-Frame-Options "DENY"
    Header always set X-Content-Type-Options "nosniff"
    Header always set Referrer-Policy "strict-origin-when-cross-origin"
    Header always set Permissions-Policy "camera=(), microphone=(), geolocation=()"
    Header always set Content-Security-Policy "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:"
</IfModule>`}
              </pre>
              <p className="mt-4 text-sm text-muted-foreground">
                Make sure <code className="rounded bg-muted px-1.5 py-0.5">mod_headers</code> is enabled: <code className="rounded bg-muted px-1.5 py-0.5">a2enmod headers && systemctl restart apache2</code>
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* WordPress */}
          <section id="wordpress" className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10">
                <svg className="h-6 w-6 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.54.82-2.771.82-3.864 0-.405-.026-.78-.07-1.109m-7.981.105c.647-.034 1.229-.102 1.229-.102.579-.068.51-.921-.068-.889 0 0-1.739.136-2.86.136-1.054 0-2.826-.136-2.826-.136-.579-.034-.648.855-.068.889 0 0 .549.068 1.129.102l1.678 4.598-2.358 7.074-3.924-11.672c.648-.034 1.229-.102 1.229-.102.579-.068.51-.921-.068-.889 0 0-1.739.138-2.86.138-.201 0-.438-.008-.69-.015C4.412 3.15 8.024 1.243 12.001 1.243c2.965 0 5.667 1.133 7.696 2.988-.049-.003-.097-.008-.146-.008-1.054 0-1.8.917-1.8 1.903 0 .884.51 1.632 1.054 2.515.408.72.884 1.64.884 2.974 0 .921-.354 1.988-.82 3.476l-1.076 3.596zm-9.461 20.33c-2.137-.662-4.039-1.848-5.549-3.418l3.015-8.75z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold">WordPress</h2>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">
              Use a security plugin or add headers via functions.php.
            </p>

            <div className="mt-8 space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">Option 1: Plugin (Recommended)</h3>
                <p className="mt-2 text-muted-foreground">
                  Install a security plugin like <strong>Headers Security Advanced & HSTS WP</strong>
                  or <strong>HTTP Headers</strong> from the WordPress plugin directory. These provide
                  a GUI for configuring all security headers.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">Option 2: functions.php</h3>
                <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
{`// Add to your theme's functions.php
function add_security_headers() {
    header('Strict-Transport-Security: max-age=31536000; includeSubDomains; preload');
    header('X-Frame-Options: DENY');
    header('X-Content-Type-Options: nosniff');
    header('Referrer-Policy: strict-origin-when-cross-origin');
    header('Permissions-Policy: camera=(), microphone=(), geolocation=()');
}
add_action('send_headers', 'add_security_headers');`}
                </pre>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* CTA Section */}
          <section className="rounded-2xl bg-gradient-to-br from-teal-50 to-cyan-50 p-8 dark:from-teal-950/30 dark:to-cyan-950/30">
            <h2 className="text-2xl font-bold">Verify Your Security Headers</h2>
            <p className="mt-4 text-muted-foreground">
              After adding security headers, use our free scanner to verify they're working
              correctly. We'll check all headers and give you a score.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/tools"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-teal-600 px-6 py-3 font-semibold text-white shadow-lg shadow-teal-500/25 transition-all hover:bg-teal-700 hover:shadow-xl"
              >
                Scan Your Website
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link
                to="/guides/website-security-email-deliverability"
                className="inline-flex items-center justify-center gap-2 rounded-full border bg-background px-6 py-3 font-semibold transition-colors hover:bg-muted"
              >
                Why This Matters for Email
              </Link>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
